// Works out the wording for the availability strip in the homepage hero.
//
// availability.json holds settings, not sentences:
//   initialWeeks, assessmentWeeks, reportAfterAssessmentWeeks, therapyWait, therapyCaveat,
//   reviewedOn, liveSourceUrl (empty = live mode off).
//
// Three kinds of wording:
//   - timeless: baked into the pre-rendered HTML (Google, no-JavaScript
//     visitors). Never contains a month name.
//   - dated: shown after the page loads, from today's date in Sydney plus the
//     week settings, e.g. "mid December".
//   - live: as dated, but the initial and assessment dates come from the
//     planner's public JSON (liveSourceUrl). Used only when that data is
//     present, valid and no more than 7 days old; otherwise dated is used.
// Reports are ALWAYS the assessment date + reportAfterAssessmentWeeks.
//
// Kept free of React and browser globals (apart from fetch, passed in) so it
// can be tested with fixed dates.

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const LIVE_MAX_AGE_DAYS = 7;
const LIVE_TIMEOUT_MS = 4000;

// Add the year only when a bare month name could be read as this year's:
// the date is in a later year AND 300 or more days away. With waits of a few
// months this never happens (December -> "late January" is unambiguous).
const YEAR_AFTER_DAYS = 300;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Today's calendar date in Sydney, as { y, m (1-12), d }.
export function sydneyToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(now);
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  return { y: get('year'), m: get('month'), d: get('day') };
}

// Pure calendar arithmetic (no time zones, no daylight saving surprises).
export function addDays({ y, m, d }, days) {
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

const dayNumber = ({ y, m, d }) => Date.UTC(y, m - 1, d) / 86400000;

// "2026-12-15" -> { y, m, d }, or null if it isn't a real calendar date.
export function parseDate(s) {
  const match = typeof s === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return null;
  const date = { y: +match[1], m: +match[2], d: +match[3] };
  const check = addDays(date, 0);
  return check.y === date.y && check.m === date.m && check.d === date.d ? date : null;
}

// Days 1 to 10 "early", 11 to 20 "mid", 21 to the end "late".
export function partOfMonth(d) {
  if (d <= 10) return 'early';
  if (d <= 20) return 'mid';
  return 'late';
}

export function describeOn(today, target) {
  let text = `${partOfMonth(target.d)} ${MONTHS[target.m - 1]}`;
  if (target.y !== today.y && dayNumber(target) - dayNumber(today) >= YEAR_AFTER_DAYS) text += ` ${target.y}`;
  return text;
}

export function weeksToMonths(weeks) {
  return Math.max(1, Math.round((weeks * 7) / (365.25 / 12)));
}

// Each row is a short label on the left and a value on the right. Only the
// value changes after load, inside a fixed-width column, so nothing moves.
export function timelessText(s) {
  return {
    kind: 'timeless',
    // the hero strip's report wording, counted from today: "in about 4 months"
    reportFromNow: `in about ${plural(weeksToMonths(s.assessmentWeeks + s.reportAfterAssessmentWeeks), 'month')}`,
    initial: { label: 'Initial appointment', value: `in about ${plural(weeksToMonths(s.initialWeeks), 'month')}` },
    assessment: { label: 'Assessment sessions', value: `in about ${plural(weeksToMonths(s.assessmentWeeks), 'month')}` },
    report: { label: 'Report', value: `${plural(s.reportAfterAssessmentWeeks, 'week')} after` },
    therapyWait: s.therapyWait,
    therapyCaveat: s.therapyCaveat,
    liveNote: '',
  };
}

// Dated rows from two dates (initial, assessment). Report = assessment + N weeks.
function fromDates(s, today, initialDate, assessmentDate) {
  const reportDate = addDays(assessmentDate, s.reportAfterAssessmentWeeks * 7);
  return {
    initial: { label: 'Initial appointment', value: describeOn(today, initialDate) },
    assessment: { label: 'Assessment sessions', value: describeOn(today, assessmentDate) },
    report: { label: 'Report', value: describeOn(today, reportDate) },
  };
}

export function datedText(s, today) {
  return {
    kind: 'dated',
    ...fromDates(s, today, addDays(today, s.initialWeeks * 7), addDays(today, s.assessmentWeeks * 7)),
    therapyWait: s.therapyWait,
    therapyCaveat: s.therapyCaveat,
    liveNote: '',
  };
}

// Checks the planner's JSON. Returns the dates to use, or null (= fall back).
// Expected shape (version 1):
//   { "version": 1, "updatedAt": "2026-10-20T07:30:00+11:00",
//     "initial": { "date": "2026-12-15", "label": "..." },
//     "assessment": { "date": "2027-01-26", "label": "..." },
//     "therapyWait": "6 to 9 months", "therapyCaveat": "subject to variability" }
// Labels are optional and ignored: the site writes its own wording from the dates.
export function validLive(data, now = new Date()) {
  if (!data || typeof data !== 'object' || data.version !== 1) return null;
  const updated = Date.parse(data.updatedAt);
  if (!Number.isFinite(updated)) return null;
  const ageDays = (now.getTime() - updated) / 86400000;
  if (ageDays < -1 || ageDays > LIVE_MAX_AGE_DAYS) return null;
  const initial = parseDate(data.initial && data.initial.date);
  const assessment = parseDate(data.assessment && data.assessment.date);
  if (!initial || !assessment) return null;
  const text = (v, max) => (typeof v === 'string' && v.trim().length > 0 && v.length <= max && !/[<>]/.test(v) ? v.trim() : null);
  return {
    initial, assessment,
    therapyWait: text(data.therapyWait, 30),
    therapyCaveat: text(data.therapyCaveat, 60),
    updatedAt: new Date(updated),
  };
}

export function liveText(s, today, live) {
  const updated = sydneyToday(live.updatedAt);
  return {
    kind: 'dated',
    ...fromDates(s, today, live.initial, live.assessment),
    therapyWait: live.therapyWait || s.therapyWait,
    therapyCaveat: live.therapyCaveat || s.therapyCaveat,
    liveNote: `Live availability, updated ${updated.d} ${MONTHS[updated.m - 1]}`,
  };
}

// Fetches the planner's JSON if liveSourceUrl is set. Never throws: any
// problem (unset, network error, bad or stale data) gives null.
export async function fetchLive(url, fetchImpl, now = new Date()) {
  if (!url || typeof fetchImpl !== 'function') return null;
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), LIVE_TIMEOUT_MS) : null;
    const res = await fetchImpl(url, { cache: 'no-store', credentials: 'omit', signal: controller && controller.signal });
    if (timer) clearTimeout(timer);
    if (!res || !res.ok) return null;
    return validLive(await res.json(), now);
  } catch (e) {
    return null;
  }
}

// What the page shows after load: live if valid, otherwise dated.
export async function wordingAfterLoad(s, { now = new Date(), fetchImpl } = {}) {
  const today = sydneyToday(now);
  const live = await fetchLive(s.liveSourceUrl, fetchImpl, now);
  return live ? liveText(s, today, live) : datedText(s, today);
}
